import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Animated,
  Platform,
} from 'react-native';
import { Swipeable } from 'react-native-gesture-handler';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { Message, MessageReactionSummary, LinkPreview } from '@/services/chat/chatService';
import { FormattedMessage } from '@/components/chat/FormattedMessage';
import { ReactionBar } from './ReactionBar';
import { LinkPreviewCard } from './LinkPreviewCard';

interface MessageBubbleProps {
  message: Message;
  isCurrentUser: boolean;
  isHighlighted?: boolean;
  onTickerPress?: (ticker: string) => void;
  onReply?: (message: Message) => void;
  onReplyPress?: (messageId: string) => void;
  currentUserId?: string;
  reactions?: MessageReactionSummary;
  onToggleReaction?: (emoji: string) => void;
  previews?: LinkPreview[];
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isCurrentUser,
  isHighlighted = false,
  onTickerPress,
  onReply,
  onReplyPress,
  currentUserId,
  reactions,
  onToggleReaction,
  previews,
}) => {
  const [hovered, setHovered] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const MAX_PREVIEW_CHARS = 600;
  const MAX_PREVIEW_LINES = 8;
  const isLong = (message?.content?.length || 0) > MAX_PREVIEW_CHARS;
  const handleTickerPress = (ticker: string) => {
    // Remove $ symbol if present
    const cleanTicker = ticker.replace('$', '').toUpperCase();
    console.log(`📊 [MessageBubble] Ticker pressed: ${cleanTicker}`);

    // Call the parent callback to filter messages by this ticker
    if (onTickerPress) {
      onTickerPress(cleanTicker);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Today - show time
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }

    // Yesterday
    if (diffDays === 1) {
      return `Yesterday ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}`;
    }

    // This week - show day name
    if (diffDays < 7) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }

    // Older - show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const metadata = message.metadata as any;
  const isAIMessage = metadata?.isAI === true;
  const isTypingMessage = metadata?.isTyping === true;

  // Debug logging for AI messages
  if (metadata?.isAI) {
    console.log(`🎨 [MessageBubble] Rendering AI message:`, {
      id: message.id,
      isAI: metadata?.isAI,
      isAIMessage,
      query: metadata?.query,
      hasQuery: !!metadata?.query,
      isCurrentUser,
    });
  }

  const renderContent = () => {
    // Show typing indicator for AI
    if (isTypingMessage) {
      return (
        <View style={styles.typingContainer}>
          <Text style={styles.typingText}>AI is thinking</Text>
          <View style={styles.typingDots}>
            <View style={styles.typingDot} />
            <View style={styles.typingDot} />
            <View style={styles.typingDot} />
          </View>
        </View>
      );
    }

    // AI message - use FormattedMessage for markdown rendering
    if (isAIMessage) {
      // Collapsed preview for very long AI messages (personal view)
      if (isLong && !expanded) {
        return (
          <>
            <Text
              style={[styles.messageText, isCurrentUser && styles.messageTextOwn]}
              numberOfLines={MAX_PREVIEW_LINES}
            >
              {message.content}
            </Text>
            <TouchableOpacity onPress={() => setExpanded(true)} activeOpacity={0.7}>
              <Text style={styles.viewMore}>View more</Text>
            </TouchableOpacity>
          </>
        );
      }
      const mentionedUsers = metadata?.mentioned_users || [];
      return (
        <>
          <FormattedMessage
            content={message.content}
            mentionedUsers={mentionedUsers}
            currentUserId={currentUserId}
          />
          {isLong && expanded && (
            <TouchableOpacity onPress={() => setExpanded(false)} activeOpacity={0.7}>
              <Text style={styles.viewMore}>View less</Text>
            </TouchableOpacity>
          )}

          {/* Show detected tickers */}
          {metadata?.tickers && metadata.tickers.length > 0 && (
            <View style={styles.tickerBadgesContainer}>
              {metadata.tickers.map((ticker: string, index: number) => (
                <TouchableOpacity
                  key={index}
                  style={styles.tickerBadge}
                  onPress={() => handleTickerPress(ticker)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.tickerBadgeText}>${ticker}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </>
      );
    }

    // Regular message - parse tickers and mentions
    const mentionedUsers = metadata?.mentioned_users || [];
    const parts = message.content.split(/(\$[A-Z]{1,5}\b|@\w+)/g);

    return (
      <>
        {!isLong || expanded ? (
          <Text style={[styles.messageText, isCurrentUser && styles.messageTextOwn]}>
            {parts.map((part, index) => {
              if (part.startsWith('$')) {
                return (
                  <Text
                    key={index}
                    style={[styles.ticker, isCurrentUser && styles.tickerOwn]}
                    onPress={() => handleTickerPress(part)}
                  >
                    {part}
                  </Text>
                );
              } else if (part.startsWith('@')) {
                // Check if this is a valid mention
                const username = part.substring(1);
                const mention = mentionedUsers.find(
                  (m: any) => m.name.toLowerCase().replace(/\s+/g, '') === username.toLowerCase()
                );

                if (mention) {
                  const isMentioningMe = mention.user_id === currentUserId;
                  return (
                    <Text
                      key={index}
                      style={[
                        isMentioningMe ? styles.mentionMe : styles.mention,
                        isCurrentUser && styles.mentionOwn,
                      ]}
                    >
                      {part}
                    </Text>
                  );
                }
              }
              return part;
            })}
          </Text>
        ) : (
          <>
            <Text
              style={[styles.messageText, isCurrentUser && styles.messageTextOwn]}
              numberOfLines={MAX_PREVIEW_LINES}
            >
              {message.content}
            </Text>
            <TouchableOpacity onPress={() => setExpanded(true)} activeOpacity={0.7}>
              <Text style={styles.viewMore}>View more</Text>
            </TouchableOpacity>
          </>
        )}

        {/* Show detected tickers that aren't explicitly in the message */}
        {message.metadata?.extracted_tickers && message.metadata.extracted_tickers.length > 0 && (
          <View style={styles.tickerBadgesContainer}>
            {message.metadata.extracted_tickers.map((ticker, index) => (
              <TouchableOpacity
                key={index}
                style={[styles.tickerBadge, isCurrentUser && styles.tickerBadgeOwn]}
                onPress={() => handleTickerPress(ticker)}
                activeOpacity={0.7}
              >
                <Text style={[styles.tickerBadgeText, isCurrentUser && styles.tickerBadgeTextOwn]}>
                  ${ticker}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </>
    );
  };

  const handleLongPress = () => {
    if (onReply) {
      onReply(message);
    }
  };

  const handleReplyClick = () => {
    if (message.reply_to_message_id && onReplyPress) {
      onReplyPress(message.reply_to_message_id);
    }
  };

  const swipeableRef = useRef<Swipeable>(null);

  const renderLeftAction = (
    progress: Animated.AnimatedInterpolation<number>,
    dragX: Animated.AnimatedInterpolation<number>
  ) => {
    const scale = dragX.interpolate({
      inputRange: [0, 80],
      outputRange: [0, 1],
      extrapolate: 'clamp',
    });

    return (
      <Animated.View style={[styles.swipeAction, { transform: [{ scale }] }]}>
        <Ionicons name="arrow-undo" size={24} color={Colors.accent.primary} />
      </Animated.View>
    );
  };

  return (
    <Swipeable
      ref={swipeableRef}
      friction={2}
      leftThreshold={80}
      renderLeftActions={renderLeftAction}
      onSwipeableWillOpen={() => {
        // Trigger reply
        if (onReply) {
          onReply(message);
          // Close swipeable after triggering
          swipeableRef.current?.close();
        }
      }}
      overshootLeft={false}
    >
      <Pressable
        style={[styles.container, isCurrentUser && styles.containerOwn]}
        onLongPress={handleLongPress}
        delayLongPress={500}
      >
        {/* Avatar - show robot icon for AI */}
        {!isCurrentUser && (
          <View style={[styles.avatar, isAIMessage && styles.avatarAI]}>
            {isAIMessage ? (
              <Ionicons name="sparkles" size={18} color={Colors.text.primary} />
            ) : (
              <Text style={styles.avatarText}>
                {message.user?.name?.charAt(0).toUpperCase() || '?'}
              </Text>
            )}
          </View>
        )}

        <View style={styles.messageContainer}>
          {!isCurrentUser && (
            <Text style={[styles.userName, isAIMessage && styles.userNameAI]}>
              {isAIMessage ? '🤖 AI Assistant' : message.user?.name || 'Unknown'}
            </Text>
          )}

          {/* AI Query Context (show what user asked) */}
          {isAIMessage && metadata?.query && (
            <View style={styles.aiQueryContext}>
              <Ionicons name="help-circle-outline" size={12} color={Colors.ai.primary} />
              <Text style={styles.aiQueryText} numberOfLines={1}>
                {metadata.query}
              </Text>
            </View>
          )}

          {/* Reply Preview (WhatsApp/Telegram Style) */}
          {message.replied_to_message && (
            <TouchableOpacity
              style={[styles.replyQuote, isCurrentUser && styles.replyQuoteOwn]}
              onPress={handleReplyClick}
              activeOpacity={0.7}
            >
              {/* Header: Icon + Sender Name */}
              <View style={styles.replyHeader}>
                <Ionicons
                  name="return-down-forward"
                  size={12}
                  color={isCurrentUser ? 'rgba(255,255,255,0.7)' : Colors.accent.primary}
                />
                <Text
                  style={[styles.replyName, isCurrentUser && styles.replyNameOwn]}
                  numberOfLines={1}
                >
                  {message.replied_to_message.user_name}
                </Text>
              </View>

              {/* Original message preview (2 lines) */}
              <Text
                style={[styles.replyPreview, isCurrentUser && styles.replyPreviewOwn]}
                numberOfLines={2}
              >
                {message.replied_to_message.content}
              </Text>

              {/* Show inherited ticker context */}
              {metadata?.inherited_tickers && metadata.inherited_tickers.length > 0 && (
                <View style={styles.replyTickers}>
                  {metadata.inherited_tickers.map((ticker: string, index: number) => (
                    <View key={index} style={styles.replyTickerBadge}>
                      <Text style={styles.replyTickerText}>${ticker}</Text>
                    </View>
                  ))}
                </View>
              )}
            </TouchableOpacity>
          )}

          <View
            onMouseEnter={Platform.OS === 'web' ? () => setHovered(true) : undefined}
            onMouseLeave={Platform.OS === 'web' ? () => setHovered(false) : undefined}
            style={[
              styles.bubble,
              isCurrentUser && styles.bubbleOwn,
              isAIMessage && styles.bubbleAI,
              isHighlighted && styles.bubbleHighlighted,
            ]}
            onStartShouldSetResponder={() => {
              if (pickerOpen || optionsOpen) {
                setPickerOpen(false);
                setOptionsOpen(false);
                return true;
              }
              return false;
            }}
          >
            <View style={styles.bubbleContent}>
              {renderContent()}

              {/* Timestamp inside bubble (WhatsApp/Telegram style) */}
              <View style={styles.timeContainer}>
                <Text style={[styles.time, isCurrentUser && styles.timeOwn]}>
                  {formatTime(message.created_at)}
                </Text>
              </View>
            </View>

            {/* Add Reaction button (WhatsApp-style smiley), web hover or when picker open */}
            {onToggleReaction && Platform.OS === 'web' && (hovered || pickerOpen) && (
              <TouchableOpacity
                style={[styles.addReactionBtn]}
                onPress={() => {
                  setPickerOpen(v => !v);
                  setOptionsOpen(false);
                }}
                activeOpacity={0.8}
              >
                <Ionicons name="happy-outline" size={16} color={Colors.text.tertiary} />
              </TouchableOpacity>
            )}

            {/* Options (three dots) for web hover */}
            {Platform.OS === 'web' && (hovered || optionsOpen) && (
              <>
                <TouchableOpacity
                  style={styles.optionsBtn}
                  activeOpacity={0.8}
                  onPress={() => {
                    setOptionsOpen(v => !v);
                    setPickerOpen(false);
                  }}
                >
                  <Ionicons name="ellipsis-horizontal" size={16} color={Colors.text.tertiary} />
                </TouchableOpacity>
                {optionsOpen && (
                  <View style={styles.optionsMenu}>
                    <TouchableOpacity
                      onPress={() => {
                        setOptionsOpen(false);
                        onReply && onReply(message);
                      }}
                      style={styles.optionsItem}
                    >
                      <Ionicons name="return-down-back" size={14} color={Colors.text.secondary} />
                      <Text style={styles.optionsItemText}>Reply</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            )}

            {/* Floating emoji picker above the add button */}
            {onToggleReaction && pickerOpen && (
              <View style={styles.pickerOverlay}>
                <ReactionBar
                  summary={{ counts: {}, mine: [] }}
                  onToggle={e => {
                    onToggleReaction(e);
                    setPickerOpen(false);
                  }}
                  showPicker
                  onClosePicker={() => setPickerOpen(false)}
                />
              </View>
            )}
          </View>
        </View>
      </Pressable>
      {/* Link previews */}
      {previews && previews.length > 0 && (
        <View style={styles.previewsContainer}>
          {previews.map(p => (
            <LinkPreviewCard key={p.id} preview={p} />
          ))}
        </View>
      )}

      {/* Reactions */}
      {onToggleReaction && (
        <ReactionBar
          summary={reactions}
          onToggle={e => {
            onToggleReaction(e);
            setPickerOpen(false);
          }}
          showPicker={pickerOpen}
          onClosePicker={() => setPickerOpen(false)}
        />
      )}
    </Swipeable>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    marginBottom: 4, // Reduced from 16 for tighter spacing
    // paddingHorizontal is handled by the FlatList container
  },
  containerOwn: {
    flexDirection: 'row-reverse',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  avatarAI: {
    backgroundColor: Colors.ai.primary,
  },
  avatarText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  messageContainer: {
    flex: 1,
    maxWidth: '75%',
  },
  userName: {
    fontSize: 12,
    color: Colors.text.tertiary,
    marginBottom: 2, // Reduced from 4
    marginLeft: 8, // Reduced from 12
  },
  userNameAI: {
    color: Colors.ai.primary,
    fontWeight: '600',
  },
  aiQueryContext: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${Colors.ai.primary}15`,
    borderLeftWidth: 2,
    borderLeftColor: Colors.ai.primary,
    paddingHorizontal: 8, // Reduced from 10
    paddingVertical: 4, // Reduced from 6
    marginBottom: 4, // Reduced from 6
    marginLeft: 8, // Reduced from 12
    borderRadius: 6,
    gap: 4, // Reduced from 6
  },
  aiQueryText: {
    flex: 1,
    fontSize: 11,
    color: Colors.ai.primary,
    fontStyle: 'italic',
  },
  bubble: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  bubbleContent: {
    padding: 8, // Reduced padding for tighter spacing
    paddingBottom: 4, // Less bottom padding since timestamp is inside
  },
  addReactionBtn: {
    position: 'absolute',
    bottom: -8,
    right: -8,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  optionsBtn: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionsMenu: {
    position: 'absolute',
    top: -40,
    right: -8,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  optionsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  optionsItemText: {
    fontSize: 12,
    color: Colors.text.secondary,
    fontWeight: '600',
  },
  pickerOverlay: {
    position: 'absolute',
    right: -8,
    bottom: 26,
    zIndex: 5,
  },
  previewsContainer: {
    marginTop: 4,
  },
  bubbleOwn: {
    backgroundColor: Colors.accent.primary,
    borderColor: Colors.accent.primary,
  },
  bubbleAI: {
    backgroundColor: `${Colors.ai.primary}08`,
    borderColor: Colors.ai.primary,
    borderWidth: 2,
  },
  bubbleHighlighted: {
    backgroundColor: 'rgba(255, 200, 0, 0.2)',
    borderColor: 'rgba(255, 200, 0, 0.6)',
    borderWidth: 2,
  },
  typingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  typingText: {
    fontSize: 14,
    color: Colors.text.secondary,
    fontStyle: 'italic',
  },
  typingDots: {
    flexDirection: 'row',
    gap: 4,
  },
  typingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.ai.primary,
    opacity: 0.6,
  },
  messageText: {
    fontSize: 15,
    color: Colors.text.primary,
    lineHeight: 19, // Tighter line height
  },
  messageTextOwn: {
    color: Colors.text.primary,
  },
  ticker: {
    fontWeight: '700',
    color: Colors.ai.primary,
    textDecorationLine: 'underline',
    textDecorationColor: Colors.ai.primary,
  },
  tickerOwn: {
    color: '#FFFFFF',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    paddingHorizontal: 4,
    borderRadius: 4,
    textDecorationColor: '#FFFFFF',
  },
  tickerBadgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6, // Reduced from 8
  },
  tickerBadge: {
    backgroundColor: 'rgba(0, 217, 255, 0.15)',
    borderWidth: 1,
    borderColor: Colors.ai.primary,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  tickerBadgeOwn: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  tickerBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.ai.primary,
  },
  tickerBadgeTextOwn: {
    color: '#FFFFFF',
  },
  viewMore: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: '600',
    color: Colors.accent.primary,
    alignSelf: 'flex-start',
  },
  timeContainer: {
    alignItems: 'flex-end',
    marginTop: 4,
  },
  time: {
    fontSize: 10,
    color: Colors.text.tertiary,
    opacity: 0.7,
  },
  timeOwn: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  // WhatsApp/Telegram-style reply quote
  replyQuote: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderLeftWidth: 4,
    borderLeftColor: Colors.accent.primary,
    borderRadius: 8,
    padding: 8, // Reduced from 10
    marginBottom: 6, // Reduced from 8
    marginLeft: 8, // Reduced from 12
    marginRight: 8, // Reduced from 12
    gap: 3, // Reduced from 4
  },
  replyQuoteOwn: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderLeftColor: 'rgba(255, 255, 255, 0.5)',
    marginLeft: 0,
    marginRight: 0,
  },
  replyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  replyName: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.accent.primary,
    flex: 1,
  },
  replyNameOwn: {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  replyPreview: {
    fontSize: 11,
    color: Colors.text.tertiary,
    lineHeight: 15,
  },
  replyPreviewOwn: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  replyTickers: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  replyTickerBadge: {
    backgroundColor: 'rgba(0, 217, 255, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(0, 217, 255, 0.3)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  replyTickerText: {
    fontSize: 9,
    fontWeight: '700',
    color: Colors.ai.primary,
  },
  // Swipe action style
  swipeAction: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingLeft: 20,
    width: 80,
  },
  // Mention styles
  mention: {
    backgroundColor: `${Colors.accent.primary}25`,
    color: Colors.accent.primary,
    fontWeight: '600',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  mentionMe: {
    backgroundColor: `${Colors.status.success}30`,
    color: Colors.status.success,
    fontWeight: '700',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  mentionOwn: {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    color: '#FFFFFF',
  },
});
